import { Division } from '../types/recruiter'

/**
 * Recruiter subscription pricing, by the division of the school on their profile.
 *
 * Division maps to a tier rather than straight to a price so the server (tier →
 * Stripe price ID) and the client (tier → dollars) can never disagree about which
 * bracket a school falls in. Anything not explicitly listed — including a recruiter
 * with no school on file — bills at the d3 tier.
 *
 * These are limited-time intro prices. There is deliberately no list-price table
 * or feature flag here — ending the promo is a manual edit to `PRICE_TIER_USD`
 * plus a Stripe price-ID swap, done later.
 */

export type PriceTier = 'd1' | 'd2' | 'd3' | 'juco'

const DIVISION_TIERS: Record<Division, PriceTier> = {
  [Division.NCAA_D1]: 'd1',
  [Division.NCAA_D2]: 'd2',
  [Division.NCAA_D3]: 'd3',
  [Division.NAIA]: 'd2',
  [Division.NJCAA]: 'juco',
  [Division.CCCAA]: 'juco',
  [Division.NWAC]: 'juco',
  [Division.NCCAA]: 'juco',
  [Division.USCAA]: 'juco',
}

export const PRICE_TIER_USD: Record<PriceTier, number> = {
  d1: 1000,
  d2: 750,
  d3: 500,
  juco: 300,
}

export function priceTierForDivision(division: Division | null | undefined): PriceTier {
  if (!division) return 'd3'
  return DIVISION_TIERS[division] ?? 'd3'
}

/** Annual price in whole dollars for a school in this division. */
export function recruiterPriceUsd(division: Division | null | undefined): number {
  return PRICE_TIER_USD[priceTierForDivision(division)]
}

/** Lowest and highest annual price, for quoting a range before a school is known. */
export const RECRUITER_PRICE_RANGE_USD = {
  min: Math.min(...Object.values(PRICE_TIER_USD)),
  max: Math.max(...Object.values(PRICE_TIER_USD)),
}

/**
 * A limited, once-only percentage discount on a recruiter's first year, as the
 * server reports it. `active` goes false when the places run out.
 */
export interface LaunchOfferInfo {
  active: boolean
  percentOff: number | null
  /** How many places the offer has in all; null when uncapped. */
  maxRedemptions: number | null
}

/** Price in whole dollars after taking a percentage off. */
export function discountedPriceUsd(priceUsd: number, percentOff: number): number {
  return Math.round((priceUsd * (100 - percentOff)) / 100)
}

/** The chip text for a live offer, or null when there is nothing to advertise. */
export function launchOfferLabel(offer: LaunchOfferInfo | null | undefined): string | null {
  if (!offer?.active || !offer.percentOff) return null

  const places = offer.maxRedemptions ? ` · first ${offer.maxRedemptions} coaches` : ''
  return `${offer.percentOff}% off your first year${places}`
}
